import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Card, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaChartLine } from 'react-icons/fa';
import Loader from './Loader';
import { getUserDetails } from '../actions/userActions';
import GradientButton from './GradientButton';

const ProtectedRoute = ({ children, requiresPaidAccount = true, requiredUserType = null, requiredScope = 'main' }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { userInfo } = useSelector((state) => state.userLogin);
  const { user, loading: userDetailsLoading } = useSelector((state) => state.userDetails);

  // 🔄 Jeśli użytkownik jest zalogowany, ale dane szczegółowe nie są jeszcze pobrane – pobierz je
  useEffect(() => {
    if (userInfo && (!user || !user.name)) {
      dispatch(getUserDetails('profile'));
    }
  }, [dispatch, userInfo, user]);

  if (userDetailsLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loader />
      </div>
    );
  }

  if (!userInfo) {
    return <Navigate to="/login" replace />;
  }

  // ✅ Sprawdź czy użytkownik ma wymagany typ konta
    const userType = user?.user_type || userInfo?.user_type || 'worker';
    const allowedTypes = Array.isArray(requiredUserType) ? requiredUserType : (requiredUserType ? [requiredUserType] : null);
    if (allowedTypes && !allowedTypes.includes(userType)) {
      return <Navigate to="/login" replace />;
    }

  const hasAccess = requiredScope === 'expert'
    ? (user?.has_expert_access ?? userInfo?.has_expert_access ?? false)
    : (user?.has_main_access ?? userInfo?.has_main_access ?? false);

  // dodaj to z powrotem — potrzebne do wyświetlenia w JSX
  const subscription = user?.subscription || userInfo?.subscription;
  const relevantSub = requiredScope === 'expert' ? subscription?.expert : subscription?.main;
  const planName = relevantSub?.plan || 'darmowy';

  if (requiresPaidAccount && !hasAccess) {
    // ⚠️ Zamiast zwracać tylko komunikat, zwróć komunikat jako children
    // Sidebar zostanie dodany w App.js
    return (
      <div
        style={{
          minHeight: '100vh',
          paddingTop: '2rem',
          paddingBottom: '4rem',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-4"
              >
                <div style={{ fontSize: '5rem' }}>🔒</div>
                <h1 className="display-5 fw-bold">Dostęp zablokowany</h1>
                <p className="text-muted">Ta funkcjonalność wymaga planu premium</p>
              </motion.div>
              {/* Film prezentujący funkcjonalność */}
              <Card className="border-0 shadow-lg mt-4" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Body className="p-4">
                  <h5 className="mb-3 text-center fw-bold">Odkryj co zyskujesz z premium</h5>
                  <div style={{ 
                    position: 'relative',
                    width: '100%',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    backgroundColor: '#000'
                  }}>
                    <video
                      width="100%"
                      height="auto"
                      controls
                      controlsList="nodownload"
                      style={{
                        borderRadius: '15px',
                        display: 'block',
                        backgroundColor: '#000'
                      }}
                      onError={(e) => {
                        console.error('Błąd wideo:', e);
                      }}
                    >
                      <source src="/videos/Film_sigma_2.mp4" type="video/mp4" />
                      <source src={`${process.env.PUBLIC_URL}/videos/Film_sigma_2.mp4`} type="video/mp4" />
                      Twoja przeglądarka nie obsługuje odtwarzania wideo MP4. Spróbuj innej przeglądarki.
                    </video>
                  </div>
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-lg mt-4" style={{ borderRadius: '20px' }}>
                <Card.Body className="p-4 text-center">
                  <p className="mb-3">
                    Twój aktualny plan: <strong>{planName}</strong>
                  </p>
                  <div className="d-flex justify-content-evenly flex-wrap gap-2">
                    <GradientButton
                      onClick={() => navigate('/app-pricing')}
                      gradient="purple"
                    >
                      <FaChartLine className="me-2" />
                      Zobacz plany
                    </GradientButton>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ✅ Dostęp przyznany
  return children;
};

export default ProtectedRoute;